import { Card } from '@mui/material';
import { Box, Container } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import { RootState } from 'src/redux-store/appStore';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { httpRequest } from 'src/services/httpRequest';
import { getFeesForCurrentPermit } from '../common/getFeesForCurrentPermit';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import Content from './DynamicContent';
interface EditApplicationData {
  editApplicationFormData: {
    BusinessName: string;
    BusinessAddress: string;
    OwnerName: string;
    OwnerAddress: string;
    ServiceNumber: string;
    PhonNumber: string;
    ApplicantAddress: string;
    ApplicantName: string;
  };
}

const NewApplication: React.FC = () => {
  const navigate = useNavigate();
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const location = useLocation();
  const dispatch = useDispatch();
  const { townshipId, permit } = useParams();
  const { editApplicationFormData } =
    (location?.state as EditApplicationData) || {};
  const [VendingFormData, setVendingFormData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [dynamicRows, setDynamicRows] = useState([]);
  const [categoryError, setCategoryError] = useState<string>('');
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const [taxRate, setTaxRate] = useState<number>(0);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [totalFee, setTotalFee] = useState<number>(0);

  // Function to update the total fee from the Content component
  const handleTotalFeeChange = (fee: number) => {
    setFeeDetails({
      applicationFee: totalFee,
      inspectionFee: 0
    });
    setTotalFee(fee);
  };
  useEffect(() => {
    if (editApplicationFormData) {
      setVendingFormData({
        ...VendingFormData,
        ...editApplicationFormData
      });
    }
  }, [editApplicationFormData]);

  //Method to get application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      setTaxRate(feeObject?.CreditCardFee || 0);
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Method to get application fee
  useEffect(() => {
    if (permit) {
      handleGetApplicationFee();
    }
  }, []);

  //Method to handle change in form fields
  const handleChange = (val, e) => {
    if (typeof val === 'string' && val?.trim() === '') return;
    const { name } = e;

    setVendingFormData({
      ...VendingFormData,
      [name]: val
    });
  };

  //Method to proceed for payment
  const handleProceedToPay = async (data) => {
    setLoading(false);
    const rowsWithRemovedID = dynamicRows.map((item)=> {
      return {
        Fee: item?.Fee,
        Location: item?.Location,
        MachineID: item?.MachineID,
        MachineName: item?.MachineName
      }
    })
    const requestData = { ...VendingFormData, ...data, rowsWithRemovedID };
    const payload = {
      ApplicantName: requestData.ApplicantName,

      ApplicantPhoneNo: requestData.PhonNumber,
      ApplicantAddress: requestData.ApplicantAddress,
      BusinessName: requestData?.BusinessName,
      BusinessAddress: requestData?.BusinessAddress,
      OwnerName: requestData?.OwnerName,
      OwnerAddress: requestData?.OwnerAddress,
      ServiceNumber: requestData?.ServiceNumber,
      ApplicationFee: totalFee,
      vendingMachines: rowsWithRemovedID
    };

    const response = await httpRequest(
      'POST',
      `/clerk_vending/insert`,
      payload
    );
    if (response?.status === 'success') {
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: response?.data?.ApplicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review',
          permitPrefix: 'Vending'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  //Method to save as draft
  const handleInsertApplication = async (data) => {
    setLoading(true);
    const rowsWithRemovedID = dynamicRows.map((item)=> {
      return {
        Fee: item?.Fee,
        Location: item?.Location,
        MachineID: item?.MachineID,
        MachineName: item?.MachineName
      }
    })
    const requestData = { ...VendingFormData, ...data, rowsWithRemovedID };
    const payload = {
      ApplicantName: requestData.ApplicantName,

      ApplicantPhoneNo: requestData.PhonNumber,
      ApplicantAddress: requestData.ApplicantAddress,
      BusinessName: requestData?.BusinessName,
      BusinessAddress: requestData?.BusinessAddress,
      OwnerName: requestData?.OwnerName,
      OwnerAddress: requestData?.OwnerAddress,
      ServiceNumber: requestData?.ServiceNumber,
      ApplicationFee: totalFee,
      vendingMachines: rowsWithRemovedID
    };

    const response = await httpRequest(
      'POST',
      `/clerk_vending/insert`,
      payload
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      if (clickedOn === 'Save Draft') {
        setLoading(false);
        navigate(`/${townshipId}/Clerk Department/${permit}/draft-application`);
      }
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Method to handle application submittion
  const handleSubmit = async (data) => {
    dynamicRows.forEach((item: any) => {
      delete item.Add;
      delete item.changed;
    });

    const hasCategory = dynamicRows.some(
      (row) => row.MachineName && row.Location && row.Fee > 0
    );

    const invalidRow = dynamicRows.some(
      (row) => !row.MachineName || !row.Location || row.Fee <= 0
    );

    const totalFees = dynamicRows.reduce((total, row) => total + row.Fee, 0);

    if (invalidRow || !hasCategory || totalFees <= 0) {
      setCategoryError('Machine Name and Location are required.');
      return;
    }

    setCategoryError('');

    if (!categoryError) {
      if (clickedOn === 'Proceed to Payment') {
        handleProceedToPay(data);
      } else if (clickedOn === 'Save Draft') {
        handleInsertApplication(data);
      }
    }
  };

  //All fields for the form using Formik
  const allFields: Fields[] = [
    {
      fieldName: 'ApplicantName',
      fieldLabel: 'Applicant Name',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ApplicantAddress',
      fieldLabel: 'Applicant Address',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'PhonNumber',
      fieldLabel: 'Phone Number',
      fieldType: 'mobile',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'BusinessName',
      fieldLabel: 'Name of business concern',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'BusinessAddress',
      fieldLabel: 'Business Address',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'OwnerName',
      fieldLabel: 'Owner of machine(s)',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'OwnerAddress',
      fieldLabel: 'Owner Address',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },

    {
      fieldName: 'ServiceNumber',
      fieldLabel: 'Service Number',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    }
  ];

  //combined dynamic fields for PDF
  const combinedFields = [
    ...allFields,
    ...dynamicRows.map((row) => ({
      fieldName: `MachineName: ${row.MachineName}`,
      fieldLabel: `Location: ${row.Location}`,
      fieldType: 'text',
      required: true
    }))
  ];

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container className="main-table-container" sx={{ mt: 2, pb:{xs:"72px !important", sm:"auto"} }} >
      <Breadcrumb PermitName={'Clerk Vending'} PageTitle={'New Application'} />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="new"
      />
      <Card>
        <Box
          m={{ md: 3, xs: 1 }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <PdfGenerator
            PermitName={'Clerk Vending'}
            allFields={combinedFields}
          />
        </Box>

        <Box m={{ md: 3, xs: 1 }}>
          <FormikForm
            allFields={allFields}
            buttonPlacement={'right'}
            defaultEnabledButton={['Proceed to Payment', 'Approve', 'Update']}
            buttonTitle={['Cancel', 'Save Draft', 'Proceed to Payment']}
            setClickedOn={setClickedOn}
            clickProp
            formMethod={handleSubmit}
            heading={'Clerk Department'}
            content={
              <Content
                sendTotalFee={handleTotalFeeChange}
                dynamicRows={dynamicRows}
                setDynamicRows={setDynamicRows}
                categoryError={categoryError}
                setCategoryError={setCategoryError}
                setApplicationFee={() => { }}
                ApplicationFee={''}
                ApplicationStatus={''}
              />
            }
            handleOtherButton={() => {
              navigate(`/dashboard/${permit}`);
            }}
          />
        </Box>
      </Card>
    </Container>
  );
};

export default NewApplication;
